import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within CustomThemeProvider');
  }
  return context;
};

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  // LocalStorage'dan dark mode durumunu oku
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode) {
      setDarkMode(JSON.parse(savedMode));
    }
  }, []);

  // Dark mode değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    
    // Body class'ına da ekle (CSS için)
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Light theme (mevcut renkler)
  const lightTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#4294e3', // Mevcut mavi
      },
      secondary: {
        main: '#ff6b35',
      },
      background: {
        default: '#fafafa',
        paper: '#ffffff',
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
      },
      error: {
        main: '#f44336',
      },
      warning: {
        main: '#ff9800',
      },
      success: {
        main: '#4caf50',
      }
    },
    typography: {
      fontFamily: [
        'Roboto',
        'Heebo',
        'Inter',
        'sans-serif'
      ].join(','),
    }
  });

  // Dark theme
  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#5a6c7d', // Antrasit mavi
      },
      secondary: {
        main: '#ff8a65',
      },
      background: {
        default: '#2a2d32', // Ana background - antrasit
        paper: '#363a42', // Kart background - daha açık antrasit
      },
      text: {
        primary: '#e8eaed', // Yumuşak beyaz
        secondary: '#9aa0a6', // Yumuşak gri
      },
      error: {
        main: '#ff5252',
      },
      warning: {
        main: '#ffcc02',
      },
      success: {
        main: '#69f0ae',
      }
    },
    typography: {
      fontFamily: 'sans-serif', // Dark mode'da sadece sans-serif
    },
    components: {
      // Dark mode için component overrides
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: '#3c4043', // Kart background
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: '#e8eaed'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: '#363a42', // Paper background
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: '#2f3239', // Sidebar background
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#2f3239', // AppBar background
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }
        }
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: '#2f3239', // Toolbar background
            color: '#e8eaed'
          }
        }
      }
    }
  });

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <div style={{ 
          minHeight: '100vh',
          transition: 'all 0.3s ease',
          background: darkMode 
            ? 'linear-gradient(135deg, #2a2d32 0%, #363a42 100%)'
            : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
        }}>
          {children}
        </div>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};